import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTranslation } from '../i18n';
import { apiErrorMessage } from '../lib/errors';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogoMark } from '../components/icons';

/** Mirrors the server-side policy in worker/utils/validation.ts. */
const PASSWORD_MIN_LENGTH = 8;

export default function RegisterPage() {
  const { register, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  function validate(): string | null {
    if (!email.trim()) return t('validation.emailRequired');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) return t('validation.emailInvalid');
    if (password.length < PASSWORD_MIN_LENGTH) {
      return t('validation.passwordMin', { min: PASSWORD_MIN_LENGTH });
    }
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      return t('validation.passwordComplexity');
    }
    if (password !== confirmPassword) return t('validation.passwordMismatch');
    return null;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await register({
        email: email.trim(),
        password,
        displayName: displayName.trim() || undefined,
      });
      navigate('/dashboard', { replace: true });
    } catch (caught) {
      setError(apiErrorMessage(caught, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-md flex-col px-4 py-16">
      <div className="mb-6 flex flex-col items-center text-center">
        <LogoMark className="h-12 w-12" />
        <h1 className="mt-4 text-2xl font-semibold tracking-tight text-fg">{t('register.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('register.subtitle')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('register.cardTitle')}</CardTitle>
          <CardDescription>{t('register.cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            {error && <Alert tone="error">{error}</Alert>}

            <div className="space-y-1.5">
              <Label htmlFor="displayName">{t('register.displayName')}</Label>
              <Input
                id="displayName"
                name="displayName"
                type="text"
                autoComplete="nickname"
                placeholder={t('register.displayNamePlaceholder')}
                maxLength={60}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">{t('register.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">{t('register.password')}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                placeholder={t('register.passwordPlaceholder', { min: PASSWORD_MIN_LENGTH })}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword">{t('register.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder={t('register.confirmPlaceholder')}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
              />
            </div>

            <Button type="submit" size="lg" fullWidth loading={submitting}>
              {t('register.submit')}
            </Button>

            <p className="text-center text-xs text-muted">{t('register.securityNote')}</p>
          </form>
        </CardContent>
      </Card>

      <p className="mt-6 text-center text-sm text-muted">
        {t('register.haveAccount')}{' '}
        <Link to="/login" className="font-medium text-brand hover:underline">
          {t('register.signIn')}
        </Link>
      </p>
    </div>
  );
}
